import { Outlet } from 'react-router-dom';
import { useState } from 'react';
import AdminSidebar from '@/components/layout/AdminSidebar';
import { useAuthStore } from '@/store/auth.store';

/**
 * Admin layout - dedicated layout for PLATFORM_ADMIN users
 * Uses dark theme sidebar with admin-specific navigation
 */
export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const user = useAuthStore((state) => state.user);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  // Format greeting
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  
  const now = new Date();
  const dateStr = now.toLocaleDateString('fr-FR', options);

  return (
    <div className="flex h-screen bg-gradient-to-br from-neutral-100 via-neutral-50 to-neutral-100">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden transition-opacity duration-300"
          onClick={closeSidebar}
        />
      )}

      {/* Admin Sidebar */}
      <AdminSidebar
        isOpen={sidebarOpen}
        onClose={closeSidebar}
      />

      {/* Main content - account for fixed sidebar on desktop */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden lg:ml-72">
        {/* Admin Header */}
        <header className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm border-b border-neutral-200 px-4 py-3 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Left: Menu button (mobile) + Admin badge */}
            <div className="flex items-center gap-4">
              {/* Mobile menu button */}
              <button
                onClick={toggleSidebar}
                className="lg:hidden p-2 rounded-lg hover:bg-neutral-100 transition-colors"
                aria-label="Toggle sidebar"
              >
                <svg className="w-6 h-6 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              {/* Admin badge */}
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-bold rounded-full shadow-md">
                  ADMIN
                </span>
                <span className="text-neutral-600 font-medium hidden sm:block">
                  Panneau d'administration
                </span>
              </div>
            </div>

            {/* Right: Date + User info */}
            <div className="flex items-center gap-4">
              <span className="text-sm text-neutral-500 hidden md:block capitalize">
                {dateStr}
              </span>
              
              {/* User avatar */}
              <div className="flex items-center gap-2">
                {user?.profilePictureUrl ? (
                  <img 
                    src={user.profilePictureUrl} 
                    alt={user?.displayName || 'Admin'} 
                    className="w-8 h-8 rounded-full object-cover ring-2 ring-amber-500/50"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white font-bold text-sm ring-2 ring-amber-500/50">
                    {user?.displayName?.[0]?.toUpperCase() || 'A'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto relative">
          <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 1px)', backgroundSize: '24px 24px'}}></div>
          <div className="w-full px-4 py-6 sm:px-6 sm:py-8 lg:px-8 relative z-10">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
